import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, DropdownButton, Dropdown, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { API_URL, UPLOADS_API_URL } from "../config";

const unitOfMeasures = ["units", "kilograms", "liters", "meters", "tanks"];

const ProductModals = ({
  showCreateModal,
  showAddProductModal,
  handleCloseCreateModal,
  handleCloseAddProductModal,
  newProduct,
  setNewProduct,
  selectedProduct,
  setSelectedProduct,
  selectedProductID,
  setSelectedProductID,
  addQuantity,
  setAddQuantity,
  setLoading,
}) => {
  const [availableProducts, setAvailableProducts] = useState([]);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [restrictionGroups, setRestrictionGroups] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [formErrors, setFormErrors] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!newProduct.product_code) errors.product_code = "Product code is required.";
    if (!newProduct.product_name) errors.product_name = "Product name is required.";
    if (newProduct.product_name.length > 25) errors.product_name = "Product name must not exceed 25 characters.";
    if (images.length > 7) errors.images = "You cannot upload more than 7 images.";
    if (!newProduct.volume_length || !newProduct.volume_width || !newProduct.volume_height) {
      errors.volume = "All volume dimensions are required.";
    }
    if (!newProduct.estimated_weight) errors.weight = "Estimated weight is required.";
    if (!newProduct.category) errors.category = "Category is required.";
    if (!newProduct.commission) errors.commission = "Commission is required.";
    return errors;
  };

  const handleAddProductToStock = async () => {
    if (!selectedProductID || addQuantity <= 0) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Input',
        text: 'Please select a product and enter a valid quantity.',
      });
      return;
    }

    setLoading(true);
    try {
      const companyId = localStorage.getItem("company_id");
      const branchId = localStorage.getItem("branch_id");
      const dateTime = new Date().toISOString();
      const unitCost = newProduct.unit_cost;
      const sellingPrice = newProduct.selling_price;
      const productValueCost = unitCost * addQuantity;
      const productValueSellingPrice = sellingPrice * addQuantity;

      const requestBody = {
        inventory_mgt_id: null,
        company_id: companyId,
        branch_id: branchId,
        sale_records_id: null,
        product_id: selectedProductID,
        date_time: dateTime,
        qty_purchased: addQuantity,
        qty_sold: 0,
        qty_balance: addQuantity,
        product_value_cost: productValueCost,
        product_value_selling_price: productValueSellingPrice,
        unit_cost: unitCost,
        selling_price: sellingPrice,
        pic1: newProduct.images[0] ? newProduct.images[0].name : null,
        pic2: newProduct.images[1] ? newProduct.images[1].name : null,
        pic3: newProduct.images[2] ? newProduct.images[2].name : null,
        syncid: null,
      };

      const response = await fetch(`${API_URL}/inventorymgt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (response.ok) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Product added to stock successfully!',
        });
        window.location.reload();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add product to stock');
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes, restrictionGroupsRes] = await Promise.all([
          fetch(`${API_URL}/productdefinition`),
          fetch(`${API_URL}/categories`),
          fetch(`${API_URL}/crossorder`)
        ]);

        if (!productsRes.ok || !categoriesRes.ok || !restrictionGroupsRes.ok) {
          throw new Error("Failed to fetch data");
        }

        const [productsData, categoriesData, restrictionGroupsData] = await Promise.all([
          productsRes.json(),
          categoriesRes.json(),
          restrictionGroupsRes.json()
        ]);

        const companyId = localStorage.getItem("company_id");
        const branchId = localStorage.getItem("branch_id");

        const filteredProducts = productsData.filter(product =>
          product.company_id === parseInt(companyId) && product.branch_id === parseInt(branchId)
        );

        setAvailableProducts(filteredProducts);
        setCategories(categoriesData);
        setRestrictionGroups(restrictionGroupsData);

        // If category is already selected, fetch subcategories
        if (newProduct.category) {
          handleCategoryChange(newProduct.category);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch necessary data',
        });
      }
    };

    fetchData();
  }, [newProduct.category]);

  const handleCategoryChange = async (categoryId) => {
    try {
      const selectedCategory = categories.find(cat => cat.categories_id === parseInt(categoryId));
      if (selectedCategory) {
        setSelectedCategoryName(selectedCategory.category_name);
      }

      const response = await fetch(`${API_URL}/categories/getsubcategories/${categoryId}`);
      if (!response.ok) {
        throw new Error("Failed to fetch subcategories");
      }
      const data = await response.json();
      setSubCategories(data);
      setNewProduct(prev => ({
        ...prev,
        category: categoryId,
        sub_category: ""
      }));
    } catch (error) {
      console.error("Error fetching subcategories:", error);
      setSubCategories([]);
    }
  };

  const handleCreateProduct = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
        setFormErrors(errors);
        Swal.fire({
            icon: 'error',
            title: 'Validation Error',
            text: Object.values(errors).join(', '),
        });
        return;
    }

    setLoading(true);
    try {
        const companyId = localStorage.getItem("company_id");
        const branchId = localStorage.getItem("branch_id");

        if (!companyId || !branchId) {
            throw new Error('Company ID and Branch ID must be set');
        }

        const uploadedImageUrls = await uploadImages(companyId, branchId);

        const productData = {
            product_code: newProduct.product_code,
            company_id: parseInt(companyId),
            branch_id: parseInt(branchId),
            product_brand: newProduct.product_brand,
            product_name: newProduct.product_name,
            product_type: newProduct.product_type,
            category: selectedCategoryName, 
            sub_category: newProduct.sub_category,
            commission: parseFloat(newProduct.commission),
            unit_of_measure: newProduct.unit_of_measure,
            unit_size: newProduct.unit_size,
            description_notes: newProduct.description_notes,
            unit_cost: parseFloat(newProduct.unit_cost),
            selling_price: parseFloat(newProduct.selling_price),
            delivery_volume: (newProduct.volume_length * newProduct.volume_width * newProduct.volume_height),
            delivery_weight: parseFloat(newProduct.estimated_weight),
            min_order: parseInt(newProduct.min_order),
            max_order: parseInt(newProduct.max_order),
            cross_order_product_restriction_group_id: newProduct.cross_order_product_restriction_group_id,
            uploaded_product_image_ref: uploadedImageUrls[0] || null,
            uploaded_product_image_ref2: uploadedImageUrls[1] || null,
            uploaded_product_image_ref3: uploadedImageUrls[2] || null,
            uploaded_product_image_ref4: uploadedImageUrls[3] || null,
            uploaded_product_image_ref5: uploadedImageUrls[4] || null,
            uploaded_product_image_ref6: uploadedImageUrls[5] || null,
            uploaded_product_image_ref7: uploadedImageUrls[6] || null,
        };

        const response = await fetch(`${API_URL}/productdefinition`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(productData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to create product');
        }

        Swal.fire({
            icon: 'success',
            title: 'Success!',
            text: 'Product created successfully!',
        });
        
        resetNewProduct();
        handleCloseCreateModal();
        window.location.reload();
    } catch (error) {
        Swal.fire({
            icon: 'error',
            title: 'Error',
            text: error.message,
        });
    } finally {
        setLoading(false);
    }
};

  const uploadImages = async (companyId, branchId) => {
    const imageUploadPromises = Array.from(images).map(async (file) => {
      const formData = new FormData();
      formData.append('company_id', companyId);
      formData.append('branch_id', branchId);
      formData.append('image', file);

      try {
        const response = await fetch(`${UPLOADS_API_URL}/uploads`, {
          method: "POST",
          body: formData,
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || response.statusText);
        }
        
        const data = await response.json();
        return data.path;
      } catch (error) {
        console.error('Upload error:', error);
        throw new Error(`Error uploading image: ${error.message}`);
      }
    });

    return Promise.all(imageUploadPromises);
  };

  const resetNewProduct = () => {
    setNewProduct({
      product_code: "",
      product_brand: "",
      product_name: "",
      product_type: "",
      category: "",
      sub_category: "",
      commission: 0,
      unit_of_measure: "",
      unit_size: 1,
      description_notes: "",
      unit_cost: 0,
      selling_price: 0,
      volume_length: 0,
      volume_width: 0,
      volume_height: 0,
      estimated_weight: 0,
      min_order: 0,
      max_order: 0,
      cross_order_product_restriction_group_id: "",
    });
    setImages([]);
    setSelectedCategoryName("");
    setFormErrors({});
  };

  return (
    <>
      {/* Create Product Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create Product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group controlId="productCode">
                  <Form.Label>Product Code*</Form.Label>
                  <Form.Control
                    type="text"
                    value={newProduct.product_code}
                    onChange={(e) => setNewProduct({ ...newProduct, product_code: e.target.value })}
                    isInvalid={!!formErrors.product_code}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.product_code}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="productBrand">
                  <Form.Label>Product Brand</Form.Label>
                  <Form.Control
                    type="text"
                    value={newProduct.product_brand}
                    onChange={(e) => setNewProduct({ ...newProduct, product_brand: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="productName">
                  <Form.Label>Product Name*</Form.Label>
                  <Form.Control
                    type="text"
                    value={newProduct.product_name}
                    onChange={(e) => setNewProduct({ ...newProduct, product_name: e.target.value })}
                    isInvalid={!!formErrors.product_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.product_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="productType">
                  <Form.Label>Product Type*</Form.Label>
                  <Form.Control
                    as="select"
                    value={newProduct.product_type}
                    onChange={(e) => setNewProduct({ ...newProduct, product_type: e.target.value })}
                  >
                    <option value="">Select Type</option>
                    <option value="Product">Product</option>
                    <option value="Service">Service</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="category">
                  <Form.Label>Category*</Form.Label>
                  <Form.Control
                    as="select"
                    value={newProduct.category}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    isInvalid={!!formErrors.category}
                  >
                    <option value="">Select Category</option>
                    {categories.map(category => (
                      <option key={category.categories_id} value={category.categories_id}>
                        {category.category_name}
                      </option>
                    ))}
                  </Form.Control>
                  <Form.Control.Feedback type="invalid">
                    {formErrors.category}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="subCategory">
                  <Form.Label>Sub Category</Form.Label>
                  <Form.Control
                    as="select"
                    value={newProduct.sub_category}
                    onChange={(e) => setNewProduct({ ...newProduct, sub_category: e.target.value })}
                  >
                    <option value="">Select Sub Category</option>
                    {subCategories.map(subCategory => (
                      <option key={subCategory.sub_category_name} value={subCategory.sub_category_name}>
                        {subCategory.sub_category_name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="commission">
                  <Form.Label>Commission (%)*</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.commission}
                    onChange={(e) => setNewProduct({ ...newProduct, commission: parseFloat(e.target.value) || 0 })}
                    isInvalid={!!formErrors.commission}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.commission}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="unitOfMeasure">
                  <Form.Label>Unit of Measure*</Form.Label>
                  <Form.Control
                    as="select"
                    value={newProduct.unit_of_measure}
                    onChange={(e) => setNewProduct({ ...newProduct, unit_of_measure: e.target.value })}
                  >
                    <option value="">Select Unit</option>
                    {unitOfMeasures.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={4}>
                <Form.Group controlId="volumeLength">
                  <Form.Label>Volume Length (cm)*</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.volume_length}
                    onChange={(e) => setNewProduct({ ...newProduct, volume_length: parseFloat(e.target.value) || 0 })}
                    isInvalid={!!formErrors.volume}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="volumeWidth">
                  <Form.Label>Volume Width (cm)*</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.volume_width}
                    onChange={(e) => setNewProduct({ ...newProduct, volume_width: parseFloat(e.target.value) || 0 })}
                    isInvalid={!!formErrors.volume}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="volumeHeight">
                  <Form.Label>Volume Height (cm)*</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.volume_height}
                    onChange={(e) => setNewProduct({ ...newProduct, volume_height: parseFloat(e.target.value) || 0 })}
                    isInvalid={!!formErrors.volume}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.volume}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="estimatedWeight">
                  <Form.Label>Estimated Weight (kg)*</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.estimated_weight}
                    onChange={(e) => setNewProduct({ ...newProduct, estimated_weight: parseFloat(e.target.value) || 0 })}
                    isInvalid={!!formErrors.weight}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.weight}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="calculatedVolume">
                  <Form.Label>Calculated Volume (cm³)</Form.Label>
                  <Form.Control
                    type="text"
                    value={(newProduct.volume_length * newProduct.volume_width * newProduct.volume_height) || 0}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>


            <Row className="mt-3">
              <Col md={6}>
                <Form.Group controlId="minOrder">
                  <Form.Label>Minimum Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.min_order}
                    onChange={(e) => setNewProduct({ ...newProduct, min_order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group controlId="maxOrder">
                  <Form.Label>Maximum Order</Form.Label>
                  <Form.Control
                    type="number"
                    value={newProduct.max_order}
                    onChange={(e) => setNewProduct({ ...newProduct, max_order: parseInt(e.target.value) || 0 })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group controlId="crossOrderGroup" className="mt-3">
              <Form.Label>Cross Order Product Restriction Group</Form.Label>
              <Form.Control
                as="select"
                value={newProduct.cross_order_product_restriction_group_id}
                onChange={(e) => setNewProduct({
                  ...newProduct,
                  cross_order_product_restriction_group_id: parseInt(e.target.value) || null
                })}
              >
                <option value="">Select Restriction Group</option>
                {restrictionGroups.map(group => (
                  <option
                    key={group.cross_order_product_restriction_groups_id}
                    value={group.cross_order_product_restriction_groups_id}
                  >
                    {group.group_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="descriptionNotes" className="mt-3">
              <Form.Label>Description Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newProduct.description_notes}
                onChange={(e) => setNewProduct({ ...newProduct, description_notes: e.target.value })}
              />
            </Form.Group>

            <Form.Group controlId="productImages" className="mt-3">
              <Form.Label>Product Images (Max 7)*</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  if (files.length > 7) {
                    Swal.fire({
                      icon: 'error',
                      title: 'Image Limit Exceeded',
                      text: 'You can only upload up to 7 images.',
                    });
                    return;
                  }
                  setImages(files);
                  setNewProduct({ ...newProduct, images: files });
                }}
                isInvalid={!!formErrors.images}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.images}
              </Form.Control.Feedback>
            </Form.Group>

            {images.length > 0 && (
              <div className="mt-3">
                <h5>Selected Images:</h5>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  {Array.from(images).map((image, index) => (
                    <img
                      key={index}
                      src={URL.createObjectURL(image)}
                      alt={`Product ${index + 1}`}
                      style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                    />
                  ))}
                </div>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCreateModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleCreateProduct}>
            Create Product
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Add Product to Stock Modal */}
      <Modal show={showAddProductModal} onHide={handleCloseAddProductModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Product to Stock</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <InputGroup className="mb-3">
            <Form.Control
              type="text"
              placeholder="Select Product..."
              value={selectedProduct}
              readOnly
            />
            <DropdownButton
              as={InputGroup.Append}
              variant="outline-secondary"
              title="Select Product"
              id="input-group-dropdown-1"
            >
              {availableProducts.length > 0 ? (
                availableProducts.map((product) => (
                  <Dropdown.Item
                    key={product.product_id}
                    onClick={() => {
                      setSelectedProduct(product.product_name);
                      setSelectedProductID(product.product_id);
                    }}
                  >
                    {product.product_name}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item disabled>No products available</Dropdown.Item>
              )}
            </DropdownButton>
          </InputGroup>

          <Form.Group controlId="addQuantity">
            <Form.Label>Quantity to Add*</Form.Label>
            <Form.Control
              type="number"
              value={addQuantity}
              onChange={(e) => setAddQuantity(parseInt(e.target.value) || 0)}
            />
          </Form.Group>

          <Row className="mt-3">
            <Col md={6}>
              <Form.Group controlId="unitCost">
                <Form.Label>Unit Cost*</Form.Label>
                <Form.Control
                  type="number"
                  value={newProduct.unit_cost}
                  onChange={(e) => setNewProduct({ ...newProduct, unit_cost: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="sellingPrice">
                <Form.Label>Selling Price*</Form.Label>
                <Form.Control
                  type="number"
                  value={newProduct.selling_price}
                  onChange={(e) => setNewProduct({ ...newProduct, selling_price: parseFloat(e.target.value) || 0 })}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAddProductModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleAddProductToStock}>
            Add to Stock
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ProductModals;